import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Input } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { HttpStatusCode } from 'axios';
import { useIndicator } from '../../../hook/use_indicator';

import css from './index.module.scss';
import { useLoginStore } from './login_store';
import { isNotEmpty } from '../../../utils/object_utils';
import { passwordChangeLink } from '../../../constant/menulinks';
import { ApiError } from '../../../api/api';
import { Messages } from '../../../constant/messages';

/**
 * ログイン画面 レビュー用コンポーネント
 */
export const LoginView: React.FC<{ errorMessage?: string }> = ({ errorMessage }) => {
  const login = useLoginStore((selector) => selector.login);
  const reset = useLoginStore((selector) => selector.reset);
  const store = useLoginStore((selector) => selector);
  const { account, setAccountName, setPassword, passResetAccountName, setPassResetAccountName, error, passResetError, setError, setPassResetError } = useLoginStore((selector) => selector);
  const indicator = useIndicator();
  const navigator = useNavigate();
  const location = useLocation();

  const LayoutTypes = {
    LOGIN: 'LOGIN', // ログイン画面
    PASS_RESET: 'PASS_RESET', // パスワードリセット画面
    PASS_RESET_DONE: 'PASS_RESET_DONE', // パスワードリセット受付画面(送信完了)
  } as const;
  type LayoutType = (typeof LayoutTypes)[keyof typeof LayoutTypes];
  const [layout, setLayout] = useState<LayoutType>(LayoutTypes.LOGIN);

  const canLogin = useMemo(
    () => {
      return isNotEmpty(account.username) && isNotEmpty(account.password);
    },
    [account],
  );
  const canPassRest = useMemo(
    () => {
      return isNotEmpty(passResetAccountName);
    },
    [passResetAccountName],
  );

  useEffect(() => {
    if (errorMessage) {
      setError(errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (location.state?.message) {
      sessionStorage.removeItem('token');
      setError(location.state.message);
    }
  }, [location.state?.message]);

  useEffect(() => {
    if (layout === LayoutTypes.LOGIN) {
      // ログイン画面のエラーをクリア
      setError('');
      // ログイン画面の入力をクリア
      setAccountName('');
      setPassword('');
    } else {
      // パスワードリセット受付画面のエラーをクリア
      setPassResetError('');
      // パスワードリセット受付画面の入力をクリア
      setPassResetAccountName('');
    }
  }, [layout]);

  const onClick = useCallback(() => {
    if (!canLogin) {
      return;
    }
    indicator.show();
    login(navigator).finally(() => {
      indicator.hide();
    });
  }, [account]);

  const onClickPassReset = useCallback(() => {
    if (!canPassRest) {
      return;
    }
    setPassResetError('');
    indicator.show();
    reset(navigator).then(() => {
      setLayout(LayoutTypes.PASS_RESET_DONE);
    }).catch((e) => {
      if (e instanceof ApiError) {
        if (e.response.status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        } else if (e.response.status === HttpStatusCode.NotFound) {
          setPassResetError(Messages.ERR_NO_ACCOUNT);
        } else {
          window.alert(Messages.ERR_OTHER);
        }
      }
    }).finally(() => {
      indicator.hide();
    });
  }, [passResetAccountName]);

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onClick();
    }
  };
  const onKeyPressPassReset = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onClickPassReset();
    }
  };

  const showError = (() => {
    return (error === null || error === undefined || error.length === 0) ? null : (
      <div id={'error'} className={css.error}>
        {error}
      </div>
    );
  })();

  const showPassRestError = (() => {
    return (passResetError === null || passResetError === undefined || passResetError.length === 0) ? null : (
      <div id={'passResetError'} className={css.error}>
        {passResetError}
      </div>
    );
  })();

  if (layout === LayoutTypes.PASS_RESET) {
    // パスワードリセット受付画面
    return (
      <div className={css.login_body}>
        <div className={css.pass_reset_box}>
          <h1>パスワードリセット受付</h1>
          <div className={css.pass_reset_label}>
            パスワードをリセットします。<br/>
            アカウント名を入力してください。
          </div>
          {showPassRestError}
          <Input
            key={'passResetAccountName'}
            data-testid={'passResetAccountName'}
            className={css.pass_reset_input}
            size={'large'}
            onKeyPress={onKeyPressPassReset}
            onChange={(e) => store.setPassResetAccountName(e.target.value)}
            placeholder={'Account Name'}
          />
          <div className={css.pass_reset_button}>
            <Button size={'large'} onClick={() => setLayout(LayoutTypes.LOGIN)}>戻る</Button>
            <Button
              size={'large'}
              data-testid={'login'}
              loading={indicator.showing}
              type={'primary'}
              onClick={onClickPassReset}
              disabled={!canPassRest}
            >
              送信
            </Button>
          </div>
        </div>
      </div>
    );
  } if (layout === LayoutTypes.PASS_RESET_DONE) {
    // パスワードリセット受付画面(送信完了)
    return (
      <div className={css.login_body}>
        <div className={css.pass_reset_box}>
          <h1>パスワードリセット受付</h1>
          <div className={css.pass_reset_label}>
            メールの送信処理が完了しました。<br/><br/>
            事前登録されたメールアドレス宛に<br/>パスワードリセット用URLが届きます。
          </div>
          <div className={css.pass_reset_button}>
            <Button
              type={'primary'}
              size={'large'}
              onClick={() => setLayout(LayoutTypes.LOGIN)}>
              ログイン画面へ
            </Button>
          </div>
        </div>
      </div>
    );
  } 
  // ログイン画面
  return (
      <div className={css.login_body}>
        <div className={css.login_box}>
          <h1>統合管理システム</h1>
          <Avatar size={80} icon={<UserOutlined/>}/>
          {showError}
          <Input
            key={'account'}
            data-testid={'account'}
            className={css.login_input}
            size={'large'}
            onKeyPress={onKeyPress}
            onChange={(e) => store.setAccountName(e.target.value)}
            placeholder={'Account Name'}
          />
          <Input.Password
            key={'password'}
            data-testid={'password'}
            className={css.login_input}
            size={'large'}
            onKeyPress={onKeyPress}
            onChange={(e) => store.setPassword(e.target.value)}
            placeholder={'Password'}
          />
          <Button
            size={'large'}
            data-testid={'login'}
            loading={indicator.showing}
            type={'primary'}
            onClick={onClick}
            disabled={!canLogin}
          >
            Login
          </Button>
          <div>
            <Button className={css.loginLink} type={'link'} color={'primary'} onClick={() => { setLayout(LayoutTypes.PASS_RESET); }}>{passwordChangeLink.name}</Button>
          </div>
        </div>
      </div>
  );
  
};
